import { Button } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { endUserLabel } from "../lib/liveBreakStaff";
import { parseRatePercent, ratePercentText } from "../lib/liveBreaks";
import { useTRPC } from "../lib/trpc";

/**
 * Sets a host's own commission rate, or puts them back on the environment's default
 * (`liveBreaks.hosts.setRate`, admins). Refused above the maximum rate; a live break already open
 * keeps the rate it started with.
 */
export function LiveBreakHostRateDialog({
  host,
  defaultRateBps,
  maxRateBps,
  triggerVariant = "ghost",
}: {
  host: {
    endUserId: string;
    username: string | null;
    externalId: string | null;
    rateBps: number | null;
  };
  defaultRateBps: number;
  maxRateBps: number;
  /** Ghost in a table row, outline in a page header. */
  triggerVariant?: "ghost" | "outline";
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const name = endUserLabel({
    username: host.username,
    externalId: host.externalId ?? host.endUserId,
  });
  const [open, setOpen] = useState(false);
  const [useDefault, setUseDefault] = useState(host.rateBps === null);
  const [rate, setRate] = useState(ratePercentText(host.rateBps ?? defaultRateBps));
  const setRateMutation = useMutation(
    trpc.liveBreaks.hosts.setRate.mutationOptions({
      onSuccess: ({ terms }) => {
        toast.success(`${name}'s next live break starts at ${ratePercentText(terms.rateBps)}%.`);
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.users.liveBreaks.pathKey() });
        setOpen(false);
      },
    }),
  );
  const parsed = useDefault ? null : parseRatePercent(rate);
  const aboveMax = parsed !== null && parsed > maxRateBps;
  const valid = useDefault || (parsed !== null && !aboveMax);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setUseDefault(host.rateBps === null);
          setRate(ratePercentText(host.rateBps ?? defaultRateBps));
        }
      }}
    >
      <DialogTrigger render={<Button type="button" variant={triggerVariant} size="sm" />}>
        Set rate
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) setRateMutation.mutate({ endUserId: host.endUserId, rateBps: parsed });
          }}
        >
          <DialogHeader>
            <DialogTitle>{`${name}'s host rate`}</DialogTitle>
            <DialogDescription>
              The commission their next live break starts at, added on top of each box's price. Live
              breaks they have open keep their own rate.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`host-rate-default-${host.endUserId}`}
              checked={useDefault}
              onCheckedChange={(checked) => setUseDefault(checked)}
            />
            <Label htmlFor={`host-rate-default-${host.endUserId}`}>
              {`Use the default, ${ratePercentText(Math.min(defaultRateBps, maxRateBps))}%`}
            </Label>
          </div>
          {!useDefault && (
            <Field data-invalid={!valid || undefined}>
              <FieldLabel htmlFor={`host-rate-${host.endUserId}`}>Rate (%)</FieldLabel>
              <Input
                id={`host-rate-${host.endUserId}`}
                type="number"
                inputMode="decimal"
                min={0}
                max={maxRateBps / 100}
                step={0.01}
                value={rate}
                aria-invalid={!valid}
                onChange={(e) => setRate(e.target.value)}
              />
              {valid ? (
                <FieldDescription>{`0 to the maximum, ${ratePercentText(maxRateBps)}%.`}</FieldDescription>
              ) : (
                <FieldError>
                  {aboveMax
                    ? `Can't be above the maximum rate, ${ratePercentText(maxRateBps)}%.`
                    : "A percent with two decimals at most."}
                </FieldError>
              )}
            </Field>
          )}
          <DialogFooter>
            <Button type="submit" disabled={!valid || setRateMutation.isPending}>
              {setRateMutation.isPending ? "Saving…" : "Save rate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
